<template>
  <!-- O modal precisa ser filho do body. O fundo escuro do Bootstrap é anexado ao body
       com z-index 1050, e o z-index 1055 daqui só vale dentro do contexto de
       empilhamento do ancestral posicionado mais próximo. Montado dentro do cabeçalho,
       que é position: fixed com z-index 91, o modal inteiro ficava por baixo do fundo:
       dava para ler, mas nenhum clique chegava nele e a tela travava.

       O Teleport continua sendo daqui: o ModalShell não teleporta. -->
  <Teleport to="body">
    <ModalShell
      ref="shell"
      class="novidades-modal"
      size="modal-lg"
      centered
      scrollable
      :static-backdrop="false"
      keyboard
      aria-labelledby="novidades-titulo"
    >
      <!-- Cabeçalho escrito aqui em vez de usar o ModalHeader: ele cobre o caso de título
           sozinho, e este tem subtítulo. O botão de fechar é a mesma marcação dele. -->
      <template #header>
        <div>
          <h1 id="novidades-titulo" class="modal-title fs-5">Novidades</h1>
          <p class="novidades-subtitulo mb-0">{{ TITULO_DAS_NOVIDADES }}</p>
        </div>
        <button
          type="button"
          class="btn-close"
          data-bs-dismiss="modal"
          aria-label="Fechar"
          @click="fechar"
        ></button>
      </template>

      <template #body>
        <ul class="novidades-lista">
          <li v-for="novidade in NOVIDADES" :key="novidade.titulo" class="novidade">
            <i class="novidade-icone" :class="novidade.icone" aria-hidden="true"></i>
            <div>
              <h2 class="novidade-titulo">{{ novidade.titulo }}</h2>
              <p class="novidade-texto">{{ novidade.texto }}</p>
            </div>
          </li>
        </ul>
      </template>

      <!-- Um botão só, então não é o caso do ModalFooter (que sempre desenha cancelar e
           confirmar). As classes são as do botão de confirmar dele, para o botão ficar
           igual ao dos outros modais. -->
      <template #footer>
        <button
          type="button"
          class="default-btn transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 rounded-1 fs-md-15 fs-lg-16 bg-success"
          @click="fechar"
        >
          Entendi
        </button>
      </template>
    </ModalShell>
  </Teleport>
</template>

<script setup>
import { useTemplateRef } from 'vue'
import ModalShell from '@/components/common/modal/ModalShell.vue'
import { NOVIDADES, TITULO_DAS_NOVIDADES } from '@/constants/novidades.js'

const shell = useTemplateRef('shell')

const open = () => shell.value?.open()
const close = () => shell.value?.close()
const fechar = () => close()

defineExpose({ open, close })
</script>

<style scoped>
/* Os tokens semânticos do design system não têm variante de tema escuro — `--text-body` é
   sempre o mesmo. Quem vira o tema é a classe `dark` no body, que o app usa para trocar
   `--bs-modal-bg`. Então a paleta local continua existindo, só que agora cada valor sai de
   um token em vez de ser um hex escrito aqui. */
.novidades-modal {
  --novidade-texto: var(--text-body);
  --novidade-texto-fraco: var(--text-subtle);
  --novidade-linha: var(--border-default);
  --novidade-destaque: var(--action-primary);
}

body.dark .novidades-modal {
  --novidade-texto: var(--text-on-dark);
  --novidade-texto-fraco: var(--text-on-dark-muted);
  --novidade-linha: var(--border-inverse);
  /* O violeta de ação cai para 2,98:1 sobre o fundo escuro do modal, abaixo do mínimo de
     3:1 de contraste para elemento não textual. O tom claro dele passa em 3,44:1. */
  --novidade-destaque: var(--action-primary-hover);
}

.novidades-subtitulo {
  margin-top: var(--space-1);
  font-size: var(--text-base);
  color: var(--novidade-texto-fraco);
}

.novidades-lista {
  margin: 0;
  padding: 0;
  list-style: none;
}

.novidade {
  display: flex;
  gap: var(--space-4);
  padding: var(--space-4) 0;
  border-top: var(--border-width) solid var(--novidade-linha);
}

.novidade:first-child {
  border-top: none;
  padding-top: 0;
}

.novidade-icone {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  /* Uma linha do título de altura: alinha o ícone com a primeira linha sem recuo mágico */
  height: calc(var(--text-lg) * var(--leading-snug));
  font-size: var(--text-3xl);
  line-height: 1;
  color: var(--novidade-destaque);
}

.novidade-titulo {
  margin: 0 0 var(--space-1);
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
  line-height: var(--leading-snug);
  color: var(--novidade-texto);
}

.novidade-texto {
  margin: 0;
  font-size: var(--text-md);
  line-height: var(--leading-normal);
  color: var(--novidade-texto-fraco);
  text-wrap: pretty;
}
</style>
